// File: client/src/components/rules/expressionEditor/ConditionRow.js
import React, { useEffect, useRef } from 'react';
import { Row, Col, Select, Input, Button, Typography, Card } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { OPERATORS } from './constants';

const { Option } = Select;
const { Text } = Typography;

const ConditionRow = ({
                          condition,
                          onUpdate,
                          onDelete,
                          availableFields,
                          disableDelete = false,
                          onHighlight,
                          isHighlighted,
                      }) => {
    const valueInputRef = useRef(null);

    const handleChange = (key, value) => {
        // When any field changes, make this the only selected row
        if (onHighlight) {
            onHighlight(condition.id, true);
        }
        onUpdate({ ...condition, [key]: value });
    };

    // When field changes, suggest an appropriate default value
    useEffect(() => {
        if (condition.field && !condition.value) {
            const field = availableFields.find(f => f.path === condition.field);
            if (field) {
                let defaultValue = '';

                // Set sensible defaults based on type
                if (field.type === 'string') defaultValue = '';
                else if (field.type === 'number') defaultValue = '0';
                else if (field.type === 'boolean') defaultValue = 'true';

                // Only update if we have a meaningful default
                if (defaultValue !== '') {
                    onUpdate({ ...condition, value: defaultValue });
                }
            }
        }
    }, [condition.field, condition.value, availableFields, onUpdate]);

    return (
        <Card
            size="small"
            style={{
                marginBottom: '8px',
                border: isHighlighted ? '2px solid #1890ff' : '1px solid #d9d9d9',
                backgroundColor: 'white' // No background color change for selection
            }}
            bodyStyle={{ padding: '12px' }}
            onClick={(e) => {
                e.stopPropagation();
                onHighlight && onHighlight(condition.id, false); // Allow multiple selection
            }}
        >
            <Row gutter={8} align="middle">
                <Col flex="1">
                    <Select
                        showSearch
                        placeholder="Select field"
                        optionFilterProp="children"
                        style={{ width: '100%' }}
                        value={condition.field}
                        onChange={(value) => handleChange('field', value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={() => {
                            // Select ONLY this condition when focusing on this field
                            onHighlight && onHighlight(condition.id, true);
                        }}
                    >
                        {availableFields.map(field => (
                            <Option
                                key={field.path}
                                value={field.path}
                                title={`${field.path} (${field.type})`}
                            >
                                {field.path}
                                <Text type="secondary" style={{ marginLeft: 8 }}>({field.type})</Text>
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col flex="1">
                    <Select
                        placeholder="Operator"
                        style={{ width: '100%' }}
                        value={condition.operator}
                        onChange={(value) => handleChange('operator', value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={() => {
                            // Select ONLY this condition when focusing on this field
                            onHighlight && onHighlight(condition.id, true);
                        }}
                    >
                        {Object.entries(OPERATORS).map(([key, op]) => (
                            <Option key={key} value={key}>{op.label}</Option>
                        ))}
                    </Select>
                </Col>

                <Col flex="1">
                    <Input
                        placeholder="Value"
                        value={condition.value}
                        onChange={(e) => handleChange('value', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={() => {
                            // Select ONLY this condition when focusing on this field
                            onHighlight && onHighlight(condition.id, true);
                        }}
                        ref={valueInputRef}
                    />
                </Col>

                {!disableDelete && (
                    <Col flex="none">
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(condition.id);
                            }}
                            danger
                        />
                    </Col>
                )}
            </Row>
        </Card>
    );
};

export default ConditionRow;
